import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { apiRequest, msalInstance, silentAuthRedirectUri } from "../auth/msalConfig";
import { classifyHttpStatus, type AdminApiError } from "./types";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Thrown when the API responds 401 (session expired / no valid token) —
 * `admin-panel-ui` spec "Sesión expirada": the panel redirects to login
 * instead of showing a broken screen or partial data. Callers generally do
 * not need to catch this (the redirect already happened); it exists so
 * screens can bail out of further rendering/state updates cleanly.
 */
export class ApiAuthError extends Error {
  public readonly kind = "auth" as const;

  constructor() {
    super("Sesion expirada o sin autorizacion (401).");
    this.name = "ApiAuthError";
  }
}

/** Thrown for any non-2xx, non-401 response — carries the server's `{"error":"..."}` message plus a typed `kind` (task 2.1) so consumers can branch without re-parsing the raw status. */
export class ApiError extends Error {
  public readonly kind: ReturnType<typeof classifyHttpStatus>;

  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
    this.kind = classifyHttpStatus(status);
  }
}

/** Thrown when `fetch` itself rejects (offline/DNS/CORS) — no HTTP response exists to classify. */
export class NetworkError extends Error {
  public readonly kind = "network" as const;

  constructor() {
    super("No fue posible conectar con la Admin API (error de red).");
    this.name = "NetworkError";
  }
}

/** Safe same-origin MSAL `state` for 401 (task 2.3): only `pathname`+`search`, falling back to `"/"` for anything not single-slash-rooted (open-redirect defense). */
export function buildSafeReturnState(location: { pathname: string; search: string }): string {
  const isSafePathname = location.pathname.startsWith("/") && !location.pathname.startsWith("//");
  return isSafePathname ? `${location.pathname}${location.search}` : "/";
}

/**
 * Mensaje de error para mostrar en pantalla: usa el mensaje real del
 * servidor cuando el error es un `ApiError` (ya trae `{"error":"..."}`
 * del backend), y cae al mensaje generico solo para errores inesperados
 * (red caida, excepcion de JS, etc.) donde no hay nada mas especifico que decir.
 */
export function describeError(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/**
 * Acquires a bearer token for the Admin API scope (`api://.../Admin.Access`)
 * for the currently signed-in account, silently first and falling back to
 * an interactive redirect only when silent acquisition genuinely requires
 * user interaction (e.g. expired refresh token) — never on every call.
 */
async function acquireAccessToken(): Promise<string> {
  const account = msalInstance.getAllAccounts()[0];
  if (!account) {
    await msalInstance.loginRedirect(apiRequest);
    // loginRedirect navigates away; this line is unreachable in a real
    // browser but keeps the function's return type honest.
    throw new ApiAuthError();
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      ...apiRequest,
      account,
      redirectUri: silentAuthRedirectUri,
    });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.loginRedirect({ ...apiRequest, account });
      throw new ApiAuthError();
    }
    throw error;
  }
}

/**
 * Thin fetch wrapper (task 6.3): attaches `Authorization: Bearer <token>`
 * (MSAL-acquired) to every request, resolves the URL against
 * `VITE_API_BASE_URL`, and redirects to MSAL login on a 401 instead of
 * resolving with an error body.
 */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await acquireAccessToken();

  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const url = `${BASE_URL}${path}`;
  let response: Response;
  try {
    response = await fetch(url, { ...init, headers });
  } catch (error) {
    // A real abort (caller's AbortSignal fired) is not a network failure —
    // rethrow it as-is so callers (e.g. state/useQuery.ts's isAbortError)
    // can still recognize it. Everything else (DNS/offline/CORS) becomes
    // a typed "network" failure instead of an unclassified rethrow.
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    throw new NetworkError();
  }

  if (response.status === 401) {
    const state = buildSafeReturnState(window.location);
    await msalInstance.loginRedirect({ ...apiRequest, state });
    throw new ApiAuthError();
  }

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    let message = `Error ${response.status} llamando a la Admin API.`;
    try {
      const body = (await response.json()) as AdminApiError;
      if (body?.error) {
        message = body.error;
      }
    } catch {
      // Body was not JSON (or empty) — keep the generic message.
    }
    throw new ApiError(response.status, message);
  }

  const text = await response.text();
  return text.length === 0 ? (undefined as T) : (JSON.parse(text) as T);
}
